/**
 * 为目标元素生成一个描述字符串，包含标签名称、ID、CSS 类名
 *
 *  例如有以下元素
 * ```html
 * <h3 id="Value" class="highlight-spanned"></h3>
 * ```
 *  将会输出 **h3#Value.highlight-spanned**
 *
 * @param element 目标元素
 */
const getElementDescriptor = (element: Element | SVGElement): string => {
    if (!element || !element.tagName) {
        return '';
    }

    const tagName = element.tagName.toLowerCase();
    const elementId = element.id ? `#${element.id}` : '';
    let elementClassName = '';

    // eslint-disable-next-line
    if (element.className instanceof SVGAnimatedString) {
        elementClassName = element.className.baseVal;
    } else {
        elementClassName = element.className;
    }

    elementClassName = elementClassName
        ? `.${elementClassName
              .split(/\s+/)
              .map(name => name.replace(/_(.){3,}$/, ''))
              .join('.')}`
        : '';

    // 针对 HTML 和 BODY 只返回标签名称即可，因为同一个页面中不会有多个 HTML 或 BODY 出现
    if (tagName === 'html' || tagName === 'body') {
        return tagName;
    }

    return `${element.tagName.toLocaleLowerCase()}${elementId}${elementClassName}`;
};

/**
 * 为目标元素生成一个由外及内的层级路径
 *
 * 假设有以下结构：
 * ```html
 * <html lang="en">
 *   <body>
 *     <div id="content">
 *       <p class="title">this is title</p>
 *     </div>
 *   </body>
 * </html>
 * ```
 *
 *  在 **p** 元素上调用该方法将返回 **html > body > div#content > p.title**
 *
 * @param element 目标元素
 */

export default function generateDomPathForElement(element: Element): string {
    const result: string[] = [];

    let descriptor = getElementDescriptor(element);
    let currentElement: Element | null;
    result.push(descriptor);
    currentElement = element.parentNode as Element;
    while (currentElement) {
        descriptor = getElementDescriptor(currentElement);

        if (descriptor) {
            result.push(descriptor);
            currentElement = currentElement.parentNode as Element;
        } else {
            break;
        }
    }

    return result
        .reverse()
        .filter(name => name !== 'body' && name !== 'html')
        .join(' > ');
}
